import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PaperSegmentedControlComponent } from '@ricoh-paper/angular';
import { LocaleService } from '../../../../../core/services/locale.service';
import { CMDK_SCOPES } from '../models/cmdk.model';
import type { CmdkScope } from '../models/cmdk.model';
import { CmdkStateService } from '../services/cmdk-state.service';

/**
 * The scope chips — All · Spaces · People · Apps · Actions · News & docs ·
 * Go to. No Widgets chip: the old Home's widget dashboard is retiring (Nath,
 * 4 Oct), so search neither lists widgets nor offers to add one.
 *
 * A chip with an EMPTY QUERY browses that catalogue rather than showing
 * nothing. That is the whole reason the chips exist: they turn the palette
 * from a search box into a directory you can walk.
 *
 * Composed from `<paper-segmented-control>` rather than hand-rolled, which
 * buys the roving tabindex, Home/End and the icon-only compaction the design
 * system already implements — and keeps the palette out of the `raw_buttons`
 * ratchet, which has zero headroom in `features/shell`.
 */
@Component({
  selector: 'im-cmdk-scope-chips',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperSegmentedControlComponent],
  templateUrl: './cmdk-scope-chips.component.html',
  styleUrl: './cmdk-scope-chips.component.scss',
})
export class CmdkScopeChipsComponent {
  private readonly state = inject(CmdkStateService);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  protected readonly value = computed(() => this.state.scope() as string);

  protected readonly options = computed(() => {
    this.locale.locale();
    return CMDK_SCOPES.map(scope => ({
      value: scope,
      label: this.transloco.translate(`shell.cmdk.scope.${scope.replace('-', '_')}`),
    }));
  });

  protected readonly ariaLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.scope.aria_label');
  });

  protected onChange(value: string): void {
    this.state.setScope(value as CmdkScope);
  }
}
